import { type AncestorNode, BlockRange, type Value } from '@gitbook/slate';

import {
    BLOCK_CODE,
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_CONTENT_REF,
    BLOCK_DIVIDER,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_MATH,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABLE,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
    BLOCK_UPDATES,
    FRAGMENT_ANNOTATION_BODY,
    FRAGMENT_EXPANDABLE_BODY,
    FRAGMENT_EXPANDABLE_TITLE,
    LIST_BLOCKS,
    VOID_BLOCKS,
} from '../types';

/**
 * Blocks that can be inserted almost everywhere
 */
const ESSENTIALS_BLOCKS = [
    BLOCK_PARAGRAPH,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_DIVIDER,
    ...LIST_BLOCKS,
    BLOCK_LIST_ITEM, // List item are normalized as list
];

/**
 * Blocks that can be inserted at the top level.
 */
const TOPLEVEL_BLOCKS = [
    ...ESSENTIALS_BLOCKS,
    BLOCK_TABLE,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_CODE,
    BLOCK_EMBED,
    BLOCK_FILE,
    BLOCK_IMAGES,
    BLOCK_HINT,
    BLOCK_QUOTE,
    BLOCK_IF,
    BLOCK_TABS,
    BLOCK_EXPANDABLE,
    BLOCK_MATH,
    BLOCK_CONTENT_REF,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_DRAWING,
    BLOCK_INTEGRATION,
    BLOCK_STEPPER,
    BLOCK_UPDATES,
    BLOCK_COLUMNS,
    // Will be processed by normalization and tranformed into other blocks:
    BLOCK_IMAGE,
];

/**
 * Blocks that can be inserted in a tab item.
 */
const TABS_ITEM_BLOCKS = [
    ...ESSENTIALS_BLOCKS,
    BLOCK_CONTENT_REF,
    BLOCK_TABLE,
    BLOCK_CODE,
    BLOCK_EMBED,
    BLOCK_FILE,
    BLOCK_IMAGES,
    BLOCK_DRAWING,
    BLOCK_HINT,
    BLOCK_QUOTE,
    BLOCK_IF,
    BLOCK_MATH,
    BLOCK_INTEGRATION,
    BLOCK_REUSABLE_CONTENT,
];

/**
 * Blocks that can be inserted in a stepper step.
 */
const STEPPER_STEP_BLOCKS = [...TABS_ITEM_BLOCKS, BLOCK_EXPANDABLE, BLOCK_TABS];

/**
 * Blocks that can be inserted in an update block.
 */
const UPDATE_BLOCK_BLOCKS = [...TABS_ITEM_BLOCKS, BLOCK_EXPANDABLE, BLOCK_TABS];

/**
 * Blocks that can be inserted in a list.
 */
const LIST_ITEM_BLOCKS = [...ESSENTIALS_BLOCKS, BLOCK_IMAGES];

/**
 * Blocks that can be inserted in an if block.
 */
export const IF_BLOCK_CONTENT_BLOCKS = [
    ...ESSENTIALS_BLOCKS,
    BLOCK_CONTENT_REF,
    BLOCK_CODE,
    BLOCK_EMBED,
    BLOCK_FILE,
    BLOCK_IMAGES,
    BLOCK_DRAWING,
    BLOCK_HINT,
    BLOCK_QUOTE,
    BLOCK_IF,
    BLOCK_MATH,
    BLOCK_INTEGRATION,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_TABLE,
];

export const COLUMN_CONTENT_BLOCKS = [
    ...ESSENTIALS_BLOCKS,
    BLOCK_CODE,
    BLOCK_CONTENT_REF,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_MATH,
    BLOCK_QUOTE,
    BLOCK_TABLE,
];

const INSERTION_MAPS = {
    [BLOCK_QUOTE]: ESSENTIALS_BLOCKS.concat([BLOCK_QUOTE]),
    [BLOCK_LIST_ITEM]: LIST_ITEM_BLOCKS,
    [BLOCK_HINT]: ESSENTIALS_BLOCKS,
    [BLOCK_IF]: IF_BLOCK_CONTENT_BLOCKS,
    [BLOCK_TABS_ITEM]: TABS_ITEM_BLOCKS,
    [BLOCK_LIST_UL]: LIST_ITEM_BLOCKS,
    [BLOCK_LIST_OL]: LIST_ITEM_BLOCKS,
    [BLOCK_LIST_TASKS]: LIST_ITEM_BLOCKS,
    [BLOCK_STEPPER]: [BLOCK_STEPPER_STEP],
    [BLOCK_STEPPER_STEP]: STEPPER_STEP_BLOCKS,
    [BLOCK_UPDATES]: [BLOCK_UPDATE],
    [BLOCK_UPDATE]: UPDATE_BLOCK_BLOCKS,
    [BLOCK_COLUMN]: COLUMN_CONTENT_BLOCKS,
};

/**
 * List of blocks allowed for a fragment type.
 * If not specified, all TOPLEVEL_BLOCKS are allowed in the fragment.
 */
export const FRAGMENT_ALLOWED_BLOCKS = {
    [FRAGMENT_EXPANDABLE_TITLE]: [
        BLOCK_PARAGRAPH,
        BLOCK_HEADING_1,
        BLOCK_HEADING_2,
        BLOCK_HEADING_3,
    ],
    [FRAGMENT_EXPANDABLE_BODY]: [
        ...ESSENTIALS_BLOCKS,
        BLOCK_IMAGES,
        BLOCK_CODE,
        BLOCK_TABLE,
        BLOCK_HINT,
        BLOCK_QUOTE,
        BLOCK_IF,
        BLOCK_REUSABLE_CONTENT,
    ],
    [FRAGMENT_ANNOTATION_BODY]: [...ESSENTIALS_BLOCKS, BLOCK_CODE],
};

/**
 * Check if a block can be inserted in a parent.
 */
export function canInsertBlockIn(parent: AncestorNode, type: string): boolean {
    if (parent.object === 'document') {
        return TOPLEVEL_BLOCKS.includes(type);
    }

    if (parent.object === 'fragment') {
        const allowedBlocksInFragment =
            (parent.type ? FRAGMENT_ALLOWED_BLOCKS[parent.type] : null) || TOPLEVEL_BLOCKS;
        return allowedBlocksInFragment.includes(type);
    }

    if (VOID_BLOCKS.includes(parent.type)) {
        return false;
    }

    return INSERTION_MAPS[parent.type] ? INSERTION_MAPS[parent.type].includes(type) : false;
}

/**
 * Check if a block can be inserted at the current selection.
 */
export function canInsertBlockAt(
    value: Value,
    type: string,
    options: {
        /**
         * If true, the insertion is allowed to split the current block.
         * @default true
         */
        allowSplitBlock?: boolean;
    } = {}
): boolean {
    const { allowSplitBlock = true } = options;
    const { selection, startBlock } = value;

    if (startBlock) {
        if (!allowSplitBlock && !startBlock.isEmpty) {
            return false;
        }

        const parent = value.document.getParent(startBlock.key);
        if (parent) {
            return canInsertBlockIn(parent, type);
        }

        return false;
    }

    if (BlockRange.isBlockRange(selection) && selection.anchorBlock) {
        const parent = value.document.getParent(selection.anchorBlock);
        if (parent) {
            return canInsertBlockIn(parent, type);
        }
    }

    return false;
}
